import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  Configure,
  InstantSearch,
  useHits,
  useInstantSearch
} from 'react-instantsearch';
import { AlgoliaSearchHit } from 'components/AlgoliaSearchHit/AlgoliaSearchHit';
import { isMobile as getIsMobile } from 'utils/misc';
import searchClient, {
  createAnalyticsTags,
  userTokenAlgolia
} from 'utils/algolia';

import { ALGOLIA_INDEX_NAME, labelsAnalyticsTags } from 'constants/algolia';
import { cardVariants } from 'constants/search';
import { ANYWHERE_LOCATION_JSON, MATRIX_TYPE } from 'constants/index';

import locationStore from 'stores/LocationStore';

import * as styles from './LastCallEvents.css';
import {
  analytics,
  analyticsEvents,
  analyticsLabels,
  getParamsRailClicked
} from 'lib/analytics';
import { sendTrackingEvent } from 'lib/tracking/sendTrackingEvent';
import { resolveAnalyticsPageTag } from '../../utils/algolia';

const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

const hasSequenceOverlap = (
  base: string,
  candidate: string,
  chunkSize = 10
): boolean => {
  const baseLower = base.toLowerCase();
  const candidateLower = candidate.toLowerCase();

  if (candidateLower.length < chunkSize) {
    return baseLower.includes(candidateLower);
  }

  const limit = candidateLower.length - chunkSize;

  for (let i = 0; i <= limit; i++) {
    const chunk = candidateLower.substring(i, i + chunkSize);
    if (baseLower.includes(chunk)) {
      return true;
    }
  }

  return false;
};

function LastCallSkeleton() {
  const isMobile = getIsMobile();
  const skeletonCount = isMobile ? 3 : 6;

  return (
    <div className={styles.GridContainer}>
      {Array.from({ length: skeletonCount }).map((_, index) => (
        <div
          key={index}
          data-testid="skeleton-card"
          className={!isMobile ? styles.CardWrapper : ''}
          style={
            !isMobile
              ? { height: '114px', width: '100%', maxWidth: '582px' }
              : undefined
          }
        >
          <div className={styles.SkeletonContainer}>
            <div className={styles.SkeletonImage} />
            <div className={styles.SkeletonContent}>
              <div className={styles.SkeletonTitle} />
              <div className={styles.SkeletonDesc} />
              <div className={styles.SkeletonDate} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LastCallEventsList() {
  const { items, sendEvent } = useHits();
  const { status } = useInstantSearch();
  const isMobile = getIsMobile();
  const [visibleCardsCount, setVisibleCardsCount] = useState(isMobile ? 3 : 6);

  const hasTrackedView = useRef(false);

  useEffect(() => {
    setVisibleCardsCount(isMobile ? 3 : 6);
  }, [isMobile]);

  const handleTrackAnalyticsClick = useCallback(
    ({ eventName, eventId }: { eventName: string; eventId: string }) => {
      const payload = getParamsRailClicked({
        eventName: eventName,
        eventId: eventId,
        pageTitleForTracking: analyticsLabels.homePage,
        nameRail: analyticsLabels.lastCallEvents
      });
      sendTrackingEvent(analyticsEvents.rail_event_clicked, payload);
    },
    []
  );

  const processedItems: typeof items = useMemo(() => {
    const cleanItems: typeof items = [];
    const MAX_ITEMS_NEEDED = 6;

    for (const item of items) {
      if (cleanItems.length >= MAX_ITEMS_NEEDED) break;

      const name = (item.name as string) || '';

      const normalizedName = normalizeText(name);

      if (
        normalizedName.includes('gratis') ||
        normalizedName.includes('gratuit')
      ) {
        continue;
      }
      const isDuplicate = cleanItems.some((acceptedItem) =>
        hasSequenceOverlap((acceptedItem.name as string) || '', name, 10)
      );

      if (!isDuplicate) {
        cleanItems.push(item);
      }
    }

    return cleanItems;
  }, [items]);

  useEffect(() => {
    if (processedItems.length > 0 && !hasTrackedView.current) {
      analytics.sendEvent(analyticsEvents.rail_event_viewed, {
        item_rail_title: analyticsLabels.lastCallEvents,
        item_rail_location: analyticsLabels.homePage,
        item_rail_filter: '',
        item_collection_title: analyticsLabels.homePage,
        item_component_title: analyticsLabels.lastCallEvents
      });
      hasTrackedView.current = true;
    }
  }, [processedItems.length]);

  if (items.length === 0) {
    if (status === 'loading' || status === 'stalled') {
      return <LastCallSkeleton />;
    }
    return null;
  }

  if (processedItems.length === 0) return null;

  const toggleExpandedView = () => {
    setVisibleCardsCount((prevCount) => (prevCount === 3 ? 6 : 3));
  };

  const displayedItems = processedItems.slice(0, visibleCardsCount);
  const canExpandList = isMobile && processedItems.length > 3;

  return (
    <>
      <div className={styles.GridContainer}>
        {displayedItems.map((hit) => (
          <div
            key={hit.objectID}
            className={!isMobile ? styles.CardWrapper : ''}
          >
            <AlgoliaSearchHit
              hit={hit as IHit}
              tagCard="Tá acabando"
              tagVariant="dark"
              isHover={false}
              sendAlgoliaEvent={() => sendEvent('click', hit, 'Hit Clicked')}
              layoutCard={isMobile ? 'lateral' : 'lateral-medium'}
              tracking={{
                trackingPrefix: MATRIX_TYPE.MATRIX,
                componentTemplate: cardVariants.lastCall,
                matrizTemplate: MATRIX_TYPE.HOME,
                cardType: 'event-card',
                collectionTitle: analyticsLabels.homePage,
                componentTitle: analyticsLabels.lastCallEvents
              }}
              handleTrackAnalyticsClick={handleTrackAnalyticsClick}
            />
          </div>
        ))}
      </div>

      {canExpandList && (
        <div className={styles.ButtonContainer}>
          <button
            type="button"
            className={styles.LoadMoreButton}
            onClick={toggleExpandedView}
          >
            {visibleCardsCount === 3 ? 'Carregar mais' : 'Carregar menos'}
          </button>
        </div>
      )}
    </>
  );
}

export function LastCallEvents() {
  const userLocation = locationStore?.selectedLocation;
  const pagePath =
    typeof window !== 'undefined' ? window.location.pathname : '/';

  const { ref, inView } = useInView({ triggerOnce: true });

  const algoliaFilters = useMemo(() => {
    const oneHourCache = 3600;
    const now = Math.floor(Date.now() / 1000 / oneHourCache) * oneHourCache;
    const sevenDaysInSeconds = 7 * 24 * 60 * 60;
    const nextWeek = now + sevenDaysInSeconds;

    const filters = [
      `end_date >= ${now}`,
      `end_date <= ${nextWeek}`,
      'NOT type:ONLINE',
      'NOT type:ONDEMAND',
      'NOT is_accessible_for_free:true'
    ];

    const hasValidCity =
      userLocation?.city && userLocation.city !== ANYWHERE_LOCATION_JSON.city;

    if (hasValidCity) {
      filters.push(`city:"${userLocation.city}"`);
    }

    return filters.join(' AND ');
  }, [userLocation?.city]);

  return (
    <div
      data-testid="last-call-events-container"
      className={styles.Container}
      ref={ref}
    >
      <div className={styles.ContainerTitle}>
        <h2 className={styles.Title}>Última chamada</h2>
      </div>

      {!inView ? (
        <LastCallSkeleton />
      ) : (
        <InstantSearch
          insights={true}
          searchClient={searchClient}
          indexName={ALGOLIA_INDEX_NAME}
        >
          <Configure
            hitsPerPage={30}
            filters={algoliaFilters}
            userToken={userTokenAlgolia}
            clickAnalytics={true}
            analytics={true}
            analyticsTags={createAnalyticsTags({
              pageTag: resolveAnalyticsPageTag(pagePath),
              featureTags: [labelsAnalyticsTags.lastCallCarousel]
            })}
          />
          <LastCallEventsList />
        </InstantSearch>
      )}
    </div>
  );
}
